import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Copy, Send, Coins, MousePointerClick, CheckCircle2, Clock, TrendingUp, Gift, Sparkles, PlayCircle, Flame, ArrowRight, Instagram, Facebook, Youtube, Music2, Download, ExternalLink, X } from "lucide-react";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram, url: "https://www.instagram.com" },
  { id: "tiktok", label: "TikTok", icon: Music2, url: "https://www.tiktok.com/upload" },
  { id: "facebook", label: "Facebook", icon: Facebook, url: "https://www.facebook.com" },
  { id: "youtube", label: "YouTube", icon: Youtube, url: "https://www.youtube.com/upload" },
];

function PublishModal({ offer, open, onClose, ensureLink, url, initialPlatform }) {
  const { t } = useLang();
  const [step, setStep] = useState("platform");
  const [platform, setPlatform] = useState(initialPlatform || null);
  const [accounts, setAccounts] = useState([]);
  const [notConfigured, setNotConfigured] = useState(false);
  const [creative, setCreative] = useState((offer.creatives && offer.creatives[0]) || offer.my_creative);
  const [linkUrl, setLinkUrl] = useState(url);

  useEffect(() => {
    if (!open) return;
    api.get("/social/accounts").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    if (initialPlatform) { setPlatform(initialPlatform); setStep("content"); ensureLink().then(setLinkUrl).catch(() => {}); }
    else { setStep("platform"); setNotConfigured(false); }
  }, [open]);

  const choosePlatform = async (p) => {
    setPlatform(p);
    try {
      const { data } = await api.post(`/social/connect/${p}?offer_id=${offer.id}`);
      if (data.status === "not_configured") { setNotConfigured(true); }
      else if (data.authorize_url) { window.location.href = data.authorize_url; }
      else { setNotConfigured(true); }
    } catch (e) { toast.error(t("err_generic")); }
  };

  const goContent = async () => { setStep("content"); const u = await ensureLink(); setLinkUrl(u); };
  const copy = async (txt) => { (await copyText(txt)) ? toast.success(t("copied")) : toast.error(t("err_generic")); };
  const plat = PLATFORMS.find((x) => x.id === platform);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="publish-modal">
        {step === "platform" ? (
          <>
            <DialogHeader><DialogTitle className="font-head text-2xl">{t("pub_where")}</DialogTitle>
              <DialogDescription>{t("pub_where_sub")}</DialogDescription>
            </DialogHeader>
            {accounts.length > 0 && (
              <div className="mb-2"><div className="text-xs font-bold text-stone-400 uppercase mb-2">{t("pub_connected_accounts")}</div>
                <div className="flex flex-wrap gap-2">{accounts.map((a) => <span key={a.id} className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold capitalize">{a.provider}</span>)}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {PLATFORMS.map((p) => (
                <button key={p.id} onClick={() => choosePlatform(p.id)} data-testid={`pub-platform-${p.id}`}
                  className={`flex items-center gap-2 rounded-2xl border-2 p-4 font-bold transition-colors ${platform === p.id ? "border-primary bg-primary/5" : "border-stone-200 hover:bg-stone-50"}`}>
                  <p.icon size={20} /> {p.label}
                </button>
              ))}
            </div>
            {notConfigured && (
              <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-3 mt-1" data-testid="pub-not-configured">
                <p className="text-amber-800 text-sm font-medium">{t("pub_soon")}</p>
                <Button onClick={goContent} data-testid="pub-manual" className="mt-2 rounded-full bt-gradient text-white font-bold w-full min-h-[48px]">{t("pub_manual")}</Button>
              </div>
            )}
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-head text-2xl">{t("pub_choose_content")}</DialogTitle>
              <DialogDescription>{t("pub_step_publish").replace("{p}", plat?.label || "")}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2" data-testid="pub-content-list">
              {(offer.creatives || [offer.my_creative]).map((c, i) => (
                <button key={c.id || i} onClick={() => setCreative(c)} data-testid={`pub-creative-${i}`}
                  className={`relative rounded-xl overflow-hidden border-2 aspect-[9/16] ${creative?.id === c.id ? "border-primary" : "border-stone-200"}`}>
                  {c.image_url ? <img src={c.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bt-gradient grid place-items-center text-white text-xs p-1">{c.type}</div>}
                  {creative?.id === c.id && <span className="absolute top-1 right-1 bg-primary text-white rounded-full p-0.5"><CheckCircle2 size={14} /></span>}
                </button>
              ))}
            </div>
            {creative?.video_url && (
              <a href={creative.video_url} download target="_blank" rel="noreferrer"><Button variant="outline" data-testid="pub-download" className="w-full rounded-full font-semibold"><Download size={16} className="mr-2" />{t("pub_download")}</Button></a>
            )}
            <div>
              <div className="text-xs font-bold text-stone-400 uppercase mb-1">{t("pub_caption")}</div>
              <div className="rounded-xl border-2 border-stone-200 p-3 text-sm text-stone-600">{creative?.caption}</div>
              <Button size="sm" variant="outline" onClick={() => copy(creative?.caption)} data-testid="pub-copy-caption" className="mt-2 rounded-full font-semibold"><Copy size={13} className="mr-1" />{t("earn_copy_caption")}</Button>
            </div>
            <div>
              <div className="text-xs font-bold text-stone-400 uppercase mb-1">{t("pub_your_link")}</div>
              <div className="flex items-center gap-2 rounded-full border-2 border-primary/30 bg-primary/5 px-3 py-2">
                <Link2 size={16} className="text-primary shrink-0" /><span className="text-sm text-stone-600 truncate flex-1" data-testid="pub-link">{linkUrl}</span>
                <Button size="sm" onClick={() => copy(linkUrl)} data-testid="pub-copy-link" className="rounded-full bt-gradient text-white text-xs h-8"><Copy size={12} /></Button>
              </div>
            </div>
            <p className="text-stone-500 text-sm">{t("pub_step_publish").replace("{p}", plat?.label || "")}</p>
            <div className="flex gap-2">
              <a href={plat?.url || "#"} target="_blank" rel="noreferrer" className="flex-1"><Button data-testid="pub-open-platform" className="w-full rounded-full bt-gradient text-white font-bold min-h-[50px]"><ExternalLink size={16} className="mr-2" />{t("pub_open")} {plat?.label}</Button></a>
              <Button variant="outline" onClick={onClose} data-testid="pub-close" className="rounded-full font-semibold">{t("pub_done")}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OfferCard({ offer, onLink }) {
  const { t } = useLang();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [code, setCode] = useState(offer.my_link_code);
  const [url, setUrl] = useState(offer.my_link_code ? `${backend}/api/go/${offer.my_link_code}` : "");
  const [busy, setBusy] = useState(false);
  const cr = offer.my_creative || {};
  const caption = cr.caption || offer.ready_caption || "";

  const ensureLink = async () => {
    if (url) return url;
    const { data } = await api.post(`/offers/${offer.id}/link`);
    setCode(data.code); setUrl(data.share_url); onLink && onLink();
    return data.share_url;
  };

  const copy = async (text) => { const ok = await copyText(text); ok ? toast.success(t("copied")) : toast.error(t("err_generic")); };
  const [pubOpen, setPubOpen] = useState(false);
  const [returnPlatform, setReturnPlatform] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("oauth") === "connected" && q.get("offer") === offer.id) {
      toast.success(t("pub_connect_success"));
      setReturnPlatform(q.get("provider"));
      setPubOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  const hasVideo = cr.type === "video" && !!cr.video_url;
  const videoUrl = cr.video_url;
  const cover = cr.image_url || offer.ready_image_url || (offer.product_images && offer.product_images[0]);

  return (
    <div className="bg-white rounded-3xl border-2 border-stone-200 overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid={`offer-${offer.id}`}>
      <div className="relative">
        {hasVideo ? (
          <video src={videoUrl} muted loop playsInline className="w-full h-52 object-cover bg-black" />
        ) : cover ? (
          <img src={cover} alt={offer.title} className="w-full h-52 object-cover" />
        ) : (
          <div className="w-full h-52 bt-gradient grid place-items-center text-white text-5xl">🎁</div>
        )}
        {offer.is_new && (
          <span className="absolute top-3 left-3 bg-emerald-500 text-white text-xs font-black px-3 py-1 rounded-full shadow" data-testid={`offer-new-${offer.id}`}>{t("earn_new_badge")}</span>
        )}
        {offer.expires_soon && (
          <span className="absolute top-3 left-3 mt-8 inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-black px-2.5 py-1 rounded-full shadow" data-testid={`offer-expiring-${offer.id}`}><Clock size={12} /> {t("earn_expiring_badge")}</span>
        )}
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur">
          {hasVideo ? <PlayCircle size={14} /> : <Send size={13} />} {hasVideo ? t("earn_ready_video") : t("earn_ready_post")}
        </span>
        {offer.creatives_count > 1 && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/85 text-primary text-xs font-bold px-2.5 py-1 rounded-full backdrop-blur" data-testid={`offer-variant-${offer.id}`} title={t("earn_variant_hint")}>
            <Sparkles size={12} /> {t("earn_your_version")}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {offer.partner_name && <span className="text-xs font-bold uppercase text-stone-400 tracking-wide">{offer.partner_name}</span>}
          {offer.access_level && offer.access_level !== "free" && (
            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-black uppercase px-2 py-0.5 rounded-full" data-testid={`offer-lock-${offer.id}`}>
              🔒 {t("locked_level").replace("{level}", t("lvl_" + offer.access_level))}
            </span>
          )}
        </div>
        <h3 className="font-head font-bold text-lg mb-1">{offer.title}</h3>
        <p className="text-stone-600 text-sm flex-1">{offer.description}</p>
        {offer.conditions && <p className="text-xs text-stone-500 mt-2" data-testid={`offer-conditions-${offer.id}`}>ℹ️ {offer.conditions}</p>}

        <div className="mt-4 rounded-2xl bt-gradient text-white p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold opacity-90">{t("earn_reward_label")}</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs opacity-90">{t("reward_upto_prefix")}</span>
              <span className="font-head font-black text-2xl leading-none" data-testid={`offer-reward-${offer.id}`}>{offer.reward_display}</span>
            </div>
            <div className="text-[11px] opacity-90 mt-1">{t("reward_per_conversion")}</div>
          </div>
        </div>

        {offer.locked ? (
          <Link to="/pricing" className="mt-4 block">
            <Button data-testid={`offer-locked-${offer.id}`}
              className="w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white min-h-[54px] font-bold text-base">
              🔒 {t("offer_unlock").replace("{plan}", offer.required_plan === "determined" ? t("lvl_master") : t("lvl_pro"))}
            </Button>
          </Link>
        ) : (
          <Button onClick={() => { setReturnPlatform(null); setPubOpen(true); }} disabled={busy} data-testid={`offer-publish-${offer.id}`}
            className="mt-4 rounded-full bg-stone-900 hover:bg-stone-800 text-white min-h-[54px] font-bold text-base">
            <Send size={18} className="mr-2" /> {t("earn_publish_social")}
          </Button>
        )}
        {!offer.locked && <PublishModal offer={offer} open={pubOpen} onClose={() => setPubOpen(false)} ensureLink={ensureLink} url={url} initialPlatform={returnPlatform} />}

        {url && (
          <div className="mt-3 space-y-2" data-testid={`offer-linkbox-${offer.id}`}>
            <div className="text-xs font-semibold text-stone-400">{t("earn_link_reveal")}</div>
            <div className="flex items-center gap-2 rounded-full border-2 border-primary/30 bg-primary/5 px-3 py-2">
              <Link2 size={16} className="text-primary shrink-0" />
              <span className="text-sm text-stone-600 truncate flex-1" data-testid={`offer-link-${offer.id}`}>{url}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={() => copy(caption)} data-testid={`offer-copycaption-${offer.id}`} className="rounded-full font-semibold text-xs"><Copy size={13} className="mr-1" />{t("earn_copy_caption")}</Button>
              <Button size="sm" variant="outline" onClick={() => copy(url)} data-testid={`offer-copylink-${offer.id}`} className="rounded-full font-semibold text-xs"><Copy size={13} className="mr-1" />{t("earn_copy_link")}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HowStep({ n, icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid place-items-center w-10 h-10 rounded-2xl bg-primary/10 text-primary shrink-0"><Icon size={20} /></span>
      <div className="text-sm"><span className="font-black text-primary mr-1">{n}.</span><span className="font-semibold text-stone-700">{label}</span></div>
    </div>
  );
}

function DailyHero({ today, onCta }) {
  const { t } = useLang();
  if (!today) return null;
  const chips = [];
  if (today.new_offers > 0) chips.push({ icon: Sparkles, text: t(today.new_offers === 1 ? "today_new_offers_one" : "today_new_offers").replace("{n}", today.new_offers) });
  if (today.new_videos > 0) chips.push({ icon: PlayCircle, text: t(today.new_videos === 1 ? "today_new_videos_one" : "today_new_videos").replace("{n}", today.new_videos) });
  if (today.expiring_soon > 0) chips.push({ icon: Clock, text: t(today.expiring_soon === 1 ? "today_expiring_one" : "today_expiring").replace("{n}", today.expiring_soon) });
  const headline = today.first_visit ? t("today_first_visit")
    : today.published_today ? t("today_published_done") : t("today_publish_waiting");
  return (
    <div className="relative overflow-hidden rounded-[2rem] bt-gradient text-white p-6 sm:p-8 mb-6" data-testid="daily-hero">
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
      <div className="relative">
        <div className="text-sm font-semibold opacity-90">{t("today_greeting").replace("{name}", today.first_name || "")}</div>
        <h1 className="font-head font-black text-2xl sm:text-3xl mt-1">{t("today_title")}</h1>
        <p className="text-base sm:text-lg mt-2 font-semibold" data-testid="daily-headline">{headline}</p>
        <p className="text-sm opacity-90 mt-1">{t("today_scroll_hook")}</p>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {chips.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur text-white text-sm font-semibold px-3 py-1.5 rounded-full" data-testid={`daily-chip-${i}`}>
                <c.icon size={14} /> {c.text}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-5">
          <Button onClick={onCta} data-testid="daily-cta" className="rounded-full bg-white text-primary hover:bg-stone-100 font-bold min-h-[52px] px-6">
            {today.published_today ? t("today_cta_see") : t("today_cta_publish")} <ArrowRight size={18} className="ml-1.5" />
          </Button>
          <div className="flex gap-4 text-sm">
            <div><span className="font-head font-black text-lg">€{today.pending_amount}</span> <span className="opacity-90">{t("today_stat_pending")}</span></div>
            <div><span className="font-head font-black text-lg">€{today.earned}</span> <span className="opacity-90">{t("today_stat_earned")}</span></div>
            <div><span className="font-head font-black text-lg">{today.confirmed_sales}</span> <span className="opacity-90">{t("today_stat_conversions")}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Earn() {
  const { t } = useLang();
  const [offers, setOffers] = useState([]);
  const [newWeek, setNewWeek] = useState(0);
  const [dash, setDash] = useState(null);
  const [today, setToday] = useState(null);

  const loadOffers = () => api.get("/offers").then((r) => { setOffers(r.data.offers || []); setNewWeek(r.data.new_this_week || 0); });
  const loadDash = () => api.get("/my/affiliate").then((r) => setDash(r.data));
  const loadToday = () => api.get("/me/today").then((r) => setToday(r.data));
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    loadOffers(); loadDash();
    loadToday().then(() => api.post("/me/seen").catch(() => {}));
  }, []);
  const scrollToOffers = () => document.getElementById("offers-grid")?.scrollIntoView({ behavior: "smooth" });

  const stats = dash ? [
    { icon: MousePointerClick, label: t("earn_clicks"), value: dash.total_clicks, color: "text-sky-600 bg-sky-100" },
    { icon: CheckCircle2, label: t("earn_confirmed"), value: dash.confirmed_sales, color: "text-emerald-600 bg-emerald-100" },
    { icon: TrendingUp, label: t("earn_earned"), value: `€${dash.earned}`, color: "text-violet-600 bg-violet-100" },
    { icon: Clock, label: t("earn_pending_amount"), value: `€${dash.pending_amount}`, color: "text-amber-600 bg-amber-100" },
  ] : [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <DailyHero today={today} onCta={scrollToOffers} />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <h1 className="font-head font-black text-3xl sm:text-4xl">💰 {t("earn_today")}</h1>
        <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-bold px-3 py-1.5 rounded-full" data-testid="earn-new-week">
          <Sparkles size={15} /> {t("earn_new_week").replace("{n}", newWeek)}
        </span>
      </div>
      <p className="text-stone-600 mb-5">{t("earn_today_sub")}</p>

      <Tabs defaultValue="offers">
        <TabsList className="rounded-full bg-stone-100 p-1 mb-6">
          <TabsTrigger value="offers" data-testid="tab-offers" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-primary font-semibold px-5">{t("earn_today")}</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-primary font-semibold px-5">{t("earn_my")}</TabsTrigger>
        </TabsList>

        <TabsContent value="offers">
          <div className="rounded-3xl border-2 border-stone-200 bg-white p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-testid="earn-how">
            <div className="font-head font-bold text-stone-800">{t("earn_how_title")}</div>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
              <HowStep n={1} icon={Gift} label={t("earn_how_1")} />
              <HowStep n={2} icon={Send} label={t("earn_how_2")} />
              <HowStep n={3} icon={Coins} label={t("earn_how_3")} />
            </div>
          </div>

          {offers.length === 0 ? (
            <div className="bg-white rounded-3xl border-2 border-stone-200 p-12 text-center"><div className="text-5xl mb-4">🎁</div><p className="text-stone-600 text-lg">{t("earn_no_offers")}</p></div>
          ) : (
            <>
              <div id="offers-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start" data-testid="offers-grid">
                {offers.map((o) => <OfferCard key={o.id} offer={o} onLink={() => { loadDash(); loadToday(); }} />)}
              </div>
              <p className="text-center text-stone-500 mt-8 font-semibold">{t("earn_come_back")}</p>
              <p className="text-center text-stone-500 text-xs mt-3 max-w-2xl mx-auto" data-testid="earn-disclaimer">{t("earn_disclaimer")}</p>
            </>
          )}
        </TabsContent>

        <TabsContent value="dashboard">
          {dash && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {stats.map((s, i) => (
                  <div key={i} className="bg-white rounded-3xl border-2 border-stone-200 p-5" data-testid={`earn-stat-${i}`}>
                    <div className={`w-11 h-11 rounded-2xl grid place-items-center mb-2 ${s.color}`}><s.icon size={22} /></div>
                    <p className="font-head font-black text-2xl">{s.value}</p>
                    <p className="text-stone-500 text-sm font-medium">{s.label}</p>
                  </div>
                ))}
              </div>

              <h2 className="font-head font-bold text-xl mb-3">{t("earn_recent_sales")}</h2>
              <div className="bg-white rounded-3xl border-2 border-stone-200 divide-y divide-stone-100" data-testid="sales-list">
                {dash.sales.length === 0 && <div className="p-6 text-stone-400 text-center">{t("earn_no_sales")}</div>}
                {dash.sales.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-5 py-4">
                    <span className="font-medium">{s.offer_title}</span>
                    <span className="flex items-center gap-3">
                      <span className="font-bold text-emerald-600">€{s.amount}</span>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${s.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : s.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`}>
                        {t(`status_${s.status}`)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
